import {
  ArticleOutlined as ArticleOutlinedIcon,
  CampaignOutlined as CampaignOutlinedIcon,
  CheckCircleOutlined as CheckCircleOutlineIcon,
  Close as CloseIcon,
  DeleteOutlined as DeleteOutlineIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  OpenInNew as OpenInNewIcon,
  ReceiptLongOutlined as ReceiptLongOutlinedIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  Chip,
  ClickAwayListener,
  Divider,
  Fade,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import customerIoService, {
  cioAddMockInboxMessage,
  cioGetInboxMessages,
  cioGetInboxUnopenedCount,
  cioIsFloatingButtonVisible,
  cioOnInboxUpdates,
  cioResetMockInboxMessages,
} from './customerio';
import { type CustomerIOInboxMessage, type CustomerIOInboxProps } from './types';

const getRelativeDate = (date: Date | string): string => {
  try {
    const d = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(d.getTime())) {
      return '';
    }
    return formatDistanceToNow(d, { addSuffix: true, locale: fr });
  } catch {
    return '';
  }
};

const getPositionStyles = (position: 'bottom-left' | 'bottom-right' | 'top-right' | 'top-left') => {
  switch (position) {
    case 'bottom-right':
      return {
        button: { bottom: 24, right: 24 },
        panel: { bottom: 90, right: 24 },
      };
    case 'top-right':
      return {
        button: { top: 24, right: 24 },
        panel: { top: 86, right: 24 },
      };
    case 'top-left':
      return {
        button: { top: 24, left: 24 },
        panel: { top: 86, left: 24 },
      };
    case 'bottom-left':
    default:
      return {
        button: { bottom: 24, left: 24 },
        panel: { bottom: 90, left: 24 },
      };
  }
};

const getMessageIcon = (type?: string) => {
  switch (type) {
    case 'invoice':
      return <ReceiptLongOutlinedIcon fontSize="small" sx={{ color: '#FF9D00' }} />;
    case 'document':
      return <ArticleOutlinedIcon fontSize="small" sx={{ color: '#00C397' }} />;
    case 'announcement':
    case 'welcome':
      return <CampaignOutlinedIcon fontSize="small" sx={{ color: '#3C345A' }} />;
    default:
      return <NotificationsIcon fontSize="small" sx={{ color: '#FF9D00' }} />;
  }
};

const DEFAULT_TOPICS: string[] = [];

export const CustomerIOInbox: FC<CustomerIOInboxProps> = ({
  position = 'bottom-left',
  topics = DEFAULT_TOPICS,
  forceVisible = false,
  title = 'Notifications',
  onMessageClick,
  className,
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<CustomerIOInboxMessage[]>([]);
  const [selectedTab, setSelectedTab] = useState<0 | 1>(0);
  const [unopenedCount, setUnopenedCount] = useState<number>(0);

  const isVisible = useMemo(() => {
    return forceVisible || cioIsFloatingButtonVisible();
  }, [forceVisible]);

  // Mémoisation stable de la liste des topics pour empêcher toute boucle infinie de re-render
  const topicsKey = useMemo(
    () => (Array.isArray(topics) && topics.length > 0 ? topics.join(',') : ''),
    [topics],
  );
  const stableTopics = useMemo(
    () => (topicsKey ? topicsKey.split(',') : DEFAULT_TOPICS),
    [topicsKey],
  );

  const refreshMessages = useCallback(async () => {
    try {
      const list = await cioGetInboxMessages(...stableTopics);
      setMessages(list);
      const count = await cioGetInboxUnopenedCount(...stableTopics);
      setUnopenedCount(count);
    } catch {
      if (cioIsFloatingButtonVisible()) {
        const fallbackMocks = customerIoService.getMockInboxFiltered(stableTopics);
        setMessages(fallbackMocks);
        setUnopenedCount(fallbackMocks.filter((m) => !m.opened).length);
      }
    }
  }, [stableTopics]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    void refreshMessages();

    const unsubscribe = cioOnInboxUpdates(
      (updatedList) => {
        setMessages(updatedList);
        setUnopenedCount(updatedList.filter((m) => !m.opened).length);
      },
      ...stableTopics,
    );

    return () => {
      unsubscribe();
    };
  }, [isVisible, refreshMessages, stableTopics]);

  // Écoute de la touche Échap pour refermer la fenêtre
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const filteredMessages = useMemo(() => {
    if (selectedTab === 1) {
      return messages.filter((m) => !m.opened);
    }
    return messages;
  }, [messages, selectedTab]);

  const handleTogglePanel = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClosePanel = () => {
    setIsOpen(false);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await customerIoService.markAllInboxMessagesOpened(...stableTopics);
      await refreshMessages();
    } catch (err) {
      console.error('[Customer.io Inbox] Erreur markAllAsRead:', err);
    }
  };

  const handleToggleRead = async (message: CustomerIOInboxMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (message.opened) {
        await message.markUnopened();
      } else {
        await message.markOpened();
      }
      await refreshMessages();
    } catch (err) {
      console.error('[Customer.io Inbox] Erreur toggleRead:', err);
    }
  };

  const handleDeleteMessage = async (message: CustomerIOInboxMessage, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await message.markDeleted();
      await refreshMessages();
    } catch (err) {
      console.error('[Customer.io Inbox] Erreur deleteMessage:', err);
    }
  };

  const handleMessageAction = async (message: CustomerIOInboxMessage) => {
    try {
      message.trackClick();
      if (!message.opened) {
        await message.markOpened();
        await refreshMessages();
      }

      if (onMessageClick) {
        onMessageClick(message);
      }

      const link = message.properties?.link;
      if (link && typeof link === 'string') {
        if (link.startsWith('http://') || link.startsWith('https://')) {
          window.open(link, '_blank', 'noopener,noreferrer');
        } else {
          navigate(link);
          setIsOpen(false);
        }
      }
    } catch (err) {
      console.error('[Customer.io Inbox] Erreur messageAction:', err);
    }
  };

  const handleAddTestNotification = () => {
    cioAddMockInboxMessage();
  };

  const handleResetNotifications = () => {
    cioResetMockInboxMessages();
  };

  if (!isVisible) {
    return null;
  }

  const posStyles = getPositionStyles(position);

  return (
    <ClickAwayListener onClickAway={handleClosePanel}>
      <Box className={className} data-testid="customerio-inbox" sx={{ position: 'relative' }}>
        {/* Bouton Flottant (Triggers Inbox) */}
        <Tooltip
          title={`${title}${unopenedCount > 0 ? ` (${unopenedCount} non lue${unopenedCount > 1 ? 's' : ''})` : ''}`}
          placement="right"
        >
          <IconButton
            data-testid="customerio-inbox-trigger"
            aria-label="Boîte de réception des notifications"
            aria-expanded={isOpen}
            onClick={handleTogglePanel}
            sx={{
              position: 'fixed',
              ...posStyles.button,
              width: 56,
              height: 56,
              zIndex: 9998,
              background: 'linear-gradient(135deg, #FF9D00 0%, #FFB62D 100%)',
              color: '#FFFFFF',
              boxShadow: '0 4px 16px rgba(255, 157, 0, 0.45)',
              transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
              '&:hover': {
                background: 'linear-gradient(135deg, #F59300 0%, #FFA81B 100%)',
                transform: 'scale(1.06)',
                boxShadow: '0 6px 20px rgba(255, 157, 0, 0.6)',
              },
              '&:active': {
                transform: 'scale(0.94)',
              },
            }}
          >
            <Badge
              badgeContent={unopenedCount}
              max={99}
              sx={{
                '& .MuiBadge-badge': {
                  background: '#E53935',
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  minWidth: 18,
                  height: 18,
                  padding: '0 4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.25)',
                },
              }}
            >
              <NotificationsIcon sx={{ fontSize: 28 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Panneau Déroulant Flottant */}
        <Fade in={isOpen} timeout={200}>
          <Paper
            data-testid="customerio-inbox-panel"
            elevation={8}
            sx={{
              position: 'fixed',
              ...posStyles.panel,
              width: 380,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: 560,
              zIndex: 9999,
              borderRadius: '16px',
              border: '1px solid #ECECEC',
              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.16)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              backgroundColor: '#FFFFFF',
            }}
          >
            {/* Header du panneau */}
            <Box
              sx={{
                p: '14px 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #F0F0F0',
                background: '#FAF9F8',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'rgba(255, 157, 0, 0.14)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FF9D00',
                  }}
                >
                  <NotificationsIcon sx={{ fontSize: 18 }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2C2B2A' }}>
                  {title}
                </Typography>
                {unopenedCount > 0 && (
                  <Chip
                    label={`${unopenedCount} non lue${unopenedCount > 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      backgroundColor: 'rgba(255, 157, 0, 0.15)',
                      color: '#E07D00',
                    }}
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {unopenedCount > 0 && (
                  <Tooltip title="Tout marquer comme lu">
                    <Button
                      size="small"
                      onClick={handleMarkAllAsRead}
                      startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#6E6B68',
                        padding: '3px 8px',
                        '&:hover': {
                          color: '#FF9D00',
                          backgroundColor: 'rgba(255, 157, 0, 0.08)',
                        },
                      }}
                    >
                      Tout lire
                    </Button>
                  </Tooltip>
                )}
                <IconButton
                  size="small"
                  aria-label="Fermer"
                  onClick={handleClosePanel}
                  sx={{ color: '#888076' }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Onglets (Toutes / Non lues) */}
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
              variant="fullWidth"
              sx={{
                minHeight: 38,
                borderBottom: '1px solid #F0F0F0',
                '& .MuiTab-root': {
                  minHeight: 38,
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#7C7570',
                  '&.Mui-selected': {
                    color: '#FF9D00',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#FF9D00',
                  height: 2.5,
                },
              }}
            >
              <Tab label={`Toutes (${messages.length})`} />
              <Tab label={`Non lues (${unopenedCount})`} />
            </Tabs>

            {/* Liste des messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 200,
                maxHeight: 380,
                backgroundColor: '#FFFFFF',
              }}
            >
              {filteredMessages.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    px: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#888076',
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: '#F5F4F2',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: '#B0A9A2',
                    }}
                  >
                    <NotificationsNoneIcon sx={{ fontSize: 32 }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#3C345A', mb: 0.5 }}>
                    {selectedTab === 1
                      ? 'Aucune notification non lue'
                      : 'Aucune notification pour le moment'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888076', maxWidth: 240 }}>
                    {selectedTab === 1
                      ? 'Vous êtes à jour ! Consultez l’onglet « Toutes » pour voir l’historique.'
                      : 'Vos notifications, documents et alertes importantes s’afficheront ici.'}
                  </Typography>
                </Box>
              ) : (
                filteredMessages.map((message) => {
                  const props = message.properties || {};
                  const isUnread = !message.opened;

                  return (
                    <Box
                      key={message.messageId}
                      data-testid={`inbox-message-${message.messageId}`}
                      onClick={() => handleMessageAction(message)}
                      sx={{
                        p: '14px 16px',
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        cursor: 'pointer',
                        borderBottom: '1px solid #F4F3F1',
                        transition: 'background-color 0.15s ease',
                        backgroundColor: isUnread ? 'rgba(255, 157, 0, 0.04)' : '#FFFFFF',
                        '&:hover': {
                          backgroundColor: isUnread ? 'rgba(255, 157, 0, 0.08)' : '#F9F8F7',
                        },
                      }}
                    >
                      {/* Icône du type de notification */}
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          backgroundColor: isUnread ? 'rgba(255, 157, 0, 0.12)' : '#F2EFEB',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          mt: 0.25,
                        }}
                      >
                        {getMessageIcon(message.type)}
                      </Box>

                      {/* Contenu textuel */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {isUnread && (
                              <Box
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: '50%',
                                  backgroundColor: '#FF9D00',
                                  flexShrink: 0,
                                }}
                              />
                            )}
                            {props.isRealCustomerIO ? (
                              <Chip
                                label="Customer.io"
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.62rem',
                                  fontWeight: 700,
                                  backgroundColor: '#E8F5E9',
                                  color: '#2E7D32',
                                  borderRadius: '4px',
                                }}
                              />
                            ) : (
                              props.category && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.68rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    color: '#FF9D00',
                                  }}
                                >
                                  {String(props.category)}
                                </Typography>
                              )
                            )}
                          </Box>

                          <Typography
                            variant="caption"
                            sx={{ color: '#A09B95', fontSize: '0.7rem' }}
                          >
                            {getRelativeDate(message.sentAt)}
                          </Typography>
                        </Box>

                        {props.title && (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: isUnread ? 700 : 600,
                              color: isUnread ? '#1E1D1C' : '#4A4846',
                              mb: 0.5,
                              lineHeight: 1.35,
                            }}
                          >
                            {String(props.title)}
                          </Typography>
                        )}

                        {props.body && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              color: '#6E6B68',
                              lineHeight: 1.45,
                              mb: props.link ? 1 : 0.5,
                            }}
                          >
                            {String(props.body)}
                          </Typography>
                        )}

                        {/* Bouton d'action si un lien est fourni */}
                        {props.link && (
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleMessageAction(message);
                            }}
                            sx={{
                              mt: 0.5,
                              fontSize: '0.72rem',
                              padding: '2px 8px',
                              borderRadius: '6px',
                              borderColor: '#E0DDD8',
                              color: '#3C345A',
                              textTransform: 'none',
                              fontWeight: 600,
                              '&:hover': {
                                borderColor: '#FF9D00',
                                color: '#FF9D00',
                                backgroundColor: 'rgba(255, 157, 0, 0.05)',
                              },
                            }}
                          >
                            {String(props.actionText || 'Voir le détail')}
                          </Button>
                        )}
                      </Box>

                      {/* Actions rapides (Marquer lu/non lu, Supprimer) */}
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          alignItems: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Tooltip title={isUnread ? 'Marquer comme lu' : 'Marquer comme non lu'}>
                          <IconButton
                            size="small"
                            aria-label={isUnread ? 'Marquer comme lu' : 'Marquer comme non lu'}
                            onClick={(e) => handleToggleRead(message, e)}
                            sx={{
                              padding: '3px',
                              color: isUnread ? '#FF9D00' : '#B8B3AC',
                              '&:hover': { color: '#FF9D00' },
                            }}
                          >
                            {isUnread ? (
                              <MarkEmailReadIcon sx={{ fontSize: 16 }} />
                            ) : (
                              <MarkEmailUnreadIcon sx={{ fontSize: 16 }} />
                            )}
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Supprimer la notification">
                          <IconButton
                            size="small"
                            aria-label="Supprimer la notification"
                            onClick={(e) => handleDeleteMessage(message, e)}
                            sx={{
                              padding: '3px',
                              color: '#B8B3AC',
                              '&:hover': { color: '#E53935' },
                            }}
                          >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Footer environnement local pour tests */}
            <Divider />
            <Box
              sx={{
                p: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#FAF9F8',
              }}
            >
              <Typography variant="caption" sx={{ color: '#888076', fontSize: '0.7rem' }}>
                Mode local actif
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={handleAddTestNotification}
                  sx={{
                    fontSize: '0.68rem',
                    padding: '1px 6px',
                    textTransform: 'none',
                    color: '#6E6B68',
                    minWidth: 0,
                    '&:hover': { color: '#FF9D00' },
                  }}
                >
                  + Test
                </Button>
                <Button
                  size="small"
                  onClick={handleResetNotifications}
                  sx={{
                    fontSize: '0.68rem',
                    padding: '1px 6px',
                    textTransform: 'none',
                    color: '#888076',
                    minWidth: 0,
                    '&:hover': { color: '#E53935' },
                  }}
                >
                  Réinitialiser
                </Button>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
};

export default CustomerIOInbox;
